<template>
    <div class="analytics">
        <v-row class="analytics__date">
        <v-col cols="3">
            <v-btn variant="outlined" @click="previousDay" icon="mdi-chevron-left" />
        </v-col>
        <v-col cols="3">
            <v-menu v-model="menu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y
            min-width="auto">
            <template v-slot:activator="{ props }">
                <v-text-field v-model="formattedDate" label="Select Date" prepend-icon="mdi-calendar" readonly
                v-bind="props" />
            </template>
            <v-date-picker v-model="selectedDate" @input="menu = !menu"></v-date-picker>
            </v-menu>
        </v-col>
        <v-col cols="3">
            <v-btn variant="outlined" @click="nextDay" icon="mdi-chevron-right" />
        </v-col>
        </v-row>
        <div v-if="isLoading">
        <v-progress-circular indeterminate size="64" v-if="isLoading" color="primary" />
        </div>
        <div v-else class="analytics__contents">
            <div v-for="transcription in transcriptions" :key="transcription.id">
                <v-card variant="outlined" class="analytics__contents__card">
                    <v-col>
                        <div class="d-flex justify-end">
                            <v-btn variant="flat" @click="confirmDelete = true" icon>
                                <v-icon color="error">mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </v-col>
                    <audio class="analytics__contents__audio" :src="transcription.download_url" controls></audio>
                    <transcription-item :transcription="transcription" class="analytics__contents__card__transcriptions" />
                    <v-divider class="analytics__contents__card__divider"/>
                    <unique-word-number v-if="getWordCountByTranscriptionId(transcription.id).value"
                        :word-count="getWordCountByTranscriptionId(transcription.id).value" 
                        class="analytics__contents__card__word-counts"/>
                    <word-chart v-if="getWordCountByTranscriptionId(transcription.id).value"
                        :word-count="getWordCountByTranscriptionId(transcription.id).value" />
                    <v-divider class="analytics__contents__card__divider" />
                    <suggestion-item 
                        :suggestion="getSuggestionByTranscriptionId(transcription.id).value" 
                        :uid="uid"
                        :transcriptionId="transcription.id"
                        :selectedDate="selectedDate"
                        class="analytics__contents__card__suggestions"/>
                </v-card>
                <!-- Delete confirmation dialog -->
                <v-dialog v-model="confirmDelete" persistent max-width="300">
                <v-card>
                    <v-card-text class="text-center">
                        Will you delete the transcription?
                    </v-card-text>
                    <v-card-actions>
                        <v-btn color="primary" @click="deleteContent(transcription.id)">Yes</v-btn>
                        <v-btn color="primary" @click="confirmDelete = false">No</v-btn>
                    </v-card-actions>
                </v-card>
                </v-dialog>
                <!-- Deleting in progress dialog -->
                <v-dialog v-model="isDeleting" persistent max-width="300">
                    <v-card>
                        <v-card-text class="text-center">
                            <v-progress-circular indeterminate size="64" />
                            <div>
                            Deleting...
                            </div>
                        </v-card-text>
                    </v-card>
                </v-dialog>
            </div>
            <div v-if="transcriptions.length < 1">
                <p> No transcription available for the selected date.</p>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';

import { auth } from '@/plugins/firebase';
import TranscriptionItem from '@/components/TranscriptionItem.vue';
import WordChart from '@/components/WordChart.vue';
import UniqueWordNumber from '@/components/UniqueWordNumber.vue';
import SuggestionItem from '@/components/SuggestionItem.vue';
import { WordCount, Suggestion } from '@/models/models';

export default defineComponent({
  name: 'HomeView',
  components: {
    TranscriptionItem,
    WordChart,
    UniqueWordNumber,
    SuggestionItem,
  },
  setup() {
    const menu = ref(false);
    const store = useStore();
    const isLoading = ref(true);
    const selectedDate = ref<Date>(new Date());
    const uid = ref("");
    const confirmDelete = ref(false);
    const isDeleting = ref(false);

    const fetchTranscriptions = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        uid.value = currentUser?.uid;
        await store.dispatch('transcriptions/fetchTranscriptionsByDate', { uid: currentUser?.uid, date: selectedDate.value });
        await store.dispatch('wordCounts/fetchWordCountsByDate', { uid: currentUser?.uid, date: selectedDate.value });
        await store.dispatch('suggestions/fetchSuggestionsByDate', { uid: currentUser?.uid, date: selectedDate.value });
      } else {
        console.error('User is not authenticated.');
      }
    }
    isLoading.value = false;

    const getWordCountByTranscriptionId = (transcriptionId: string) => {
        return computed(() =>
          wordCounts.value.find((wordCount: WordCount) => wordCount.transcription_id === transcriptionId) || null
      );
    };

    const getSuggestionByTranscriptionId = (transcriptionId: string) => {
        return computed(() => 
            suggestions.value.find((suggestion: Suggestion) => suggestion.transcription_id === transcriptionId) || null
        ); 
    };

    const previousDay = () => {
      selectedDate.value = new Date(selectedDate.value.getTime() - 24 * 60 * 60 * 1000); // Subtract 1 day
    };

    const nextDay = () => {
      selectedDate.value = new Date(selectedDate.value.getTime() + 24 * 60 * 60 * 1000); // Add 1 day
    };

    const formattedDate = computed(() => {
      return moment(selectedDate.value).format('YYYY/MM/DD');
    });

    const deleteContent = async (transcriptionId: string) => {
      isDeleting.value = true;
      const currentUser = auth.currentUser;
      if (currentUser) {
        const suggestion = getWordCountByTranscriptionId(transcriptionId).value
        const wordCount = getWordCountByTranscriptionId(transcriptionId).value

        await store.dispatch('transcriptions/deleteTranscription', {
          uid: currentUser.uid,
          transcriptionId,
        })
        await store.dispatch('suggestions/deleteSuggestion', {
          uid: currentUser.uid,
          suggestionId: suggestion.id,
        })
        await store.dispatch('wordCounts/deleteWordCount', {
          uid: currentUser.uid,
          wordCountId: wordCount.id,
        })
      }

      isDeleting.value = false;
      confirmDelete.value = false;
    }

    onMounted(() => {
      fetchTranscriptions();
    });

    watch(selectedDate, (newDate) => {
      if (newDate) {
        fetchTranscriptions();
      }
    });

    const transcriptions = computed(() => store.getters['transcriptions/transcriptions']);
    const wordCounts = computed(() => store.getters['wordCounts/wordCounts']);
    const suggestions = computed(() => store.getters['suggestions/suggestions']);

    return {
      menu,
      uid,
      confirmDelete,
      transcriptions,
      isLoading,
      isDeleting,
      selectedDate,
      formattedDate,
      getWordCountByTranscriptionId,
      getSuggestionByTranscriptionId,
      fetchTranscriptions,
      deleteContent,
      previousDay,
      nextDay,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/sass/size.scss';

.analytics {
  &__date {
    justify-content: center;
  }
  &__contents {
    margin: 10px;

    &__audio {
      width: 80%;
    }
    
    &__card {
      margin: 10px;
      padding: 10px;
      
      &__transcriptions {
        margin: 10px;
      }

      &__word-counts {
        margin: 10px;
      }

      &__graph {
        margin: 10px;
      }

      &__divider {
        margin: 20px;
      }
    }
  }
}
</style>
